import { supabase } from "@/lib/supabaseClient";

// ---------------------------------------------------------------------------
// Público (landing /webinar): funciona sin iniciar sesión
// ---------------------------------------------------------------------------

export type WebinarConfig = {
  enabled: boolean;
  title: string;
  subtitle: string;
  videoUrl: string | null;
  videoDuration: number | null;
  requireRegistration: boolean;
  /** Cuándo se pide el WhatsApp: antes del video, a mitad del video o nunca. */
  captureMode: "before_video" | "mid_video" | "none";
  /** % del video en el que se pide el WhatsApp (modo mid_video). */
  captureAtPercent: number;
  sponsorName: string | null;
  refValid: boolean;
  /** Código de referido al que enviar a la persona para crear su cuenta. */
  signupRef: string | null;
};

export type WebinarLead = {
  token: string;
  name: string;
  maxPercent: number;
  position: number;
  completed: boolean;
};

export type WebinarTrackKind = "progress" | "video_started" | "video_completed" | "cta_test_clicked" | "cta_community_clicked";

const TOKEN_KEY = "vv-webinar-token";

export function getStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // sin almacenamiento: la sesión solo vive en memoria
  }
}

type RawState = { token: string; name: string; max_percent: number; position: number; completed: boolean } | null;

const toLead = (d: RawState): WebinarLead | null =>
  d ? { token: d.token, name: d.name, maxPercent: d.max_percent ?? 0, position: d.position ?? 0, completed: !!d.completed } : null;

export async function fetchWebinarConfig(ref: string | null): Promise<WebinarConfig> {
  const { data, error } = await supabase.rpc("webinar_config", { p_ref: ref });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    enabled: d.enabled === true,
    title: (d.title as string) ?? "No solo existas. Vívelo.",
    subtitle: (d.subtitle as string) ?? "",
    videoUrl: (d.video_url as string) ?? null,
    videoDuration: (d.video_duration_seconds as number) ?? null,
    requireRegistration: d.require_registration !== false,
    captureMode: d.require_registration === false ? "none" : d.capture_mode === "before_video" ? "before_video" : "mid_video",
    captureAtPercent: Math.min(90, Math.max(5, Number(d.capture_at_percent) || 25)),
    sponsorName: (d.sponsor_name as string) ?? null,
    refValid: d.ref_valid === true,
    signupRef: (d.signup_ref as string) ?? null,
  };
}

export async function registerWebinarLead(input: {
  ref: string | null;
  name: string;
  phone: string;
  country: string;
  consent: boolean;
  /** Canal de origen (whatsapp, instagram, …) tomado del link. */
  src?: string | null;
}): Promise<WebinarLead> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Bogota";
  const { data, error } = await supabase.rpc("webinar_register", {
    p_ref: input.ref,
    p_name: input.name,
    p_phone: input.phone,
    p_country: input.country,
    p_tz: tz,
    p_consent: input.consent,
    p_src: input.src || null,
  });
  if (error) {
    const msg = error.message || "";
    if (msg.includes("invalid_name")) throw new Error("Escribe tu nombre.");
    if (msg.includes("invalid_phone")) throw new Error("Revisa tu número de WhatsApp.");
    if (msg.includes("webinar_unavailable")) throw new Error("El webinar no está disponible por ahora.");
    throw new Error("No pudimos registrarte. Intenta de nuevo.");
  }
  const lead = toLead(data as RawState);
  if (!lead) throw new Error("No pudimos registrarte. Intenta de nuevo.");
  storeToken(lead.token);
  void trackProspectRegistration();
  return lead;
}

/** Estado de la verificación del WhatsApp: unverified | sent | delivered | invalid | failed | unknown. */
export type PhoneStatus = "unverified" | "sent" | "delivered" | "invalid" | "failed" | "unknown";
export type VerifyResult = { status: PhoneStatus | "unavailable"; reason?: string; iso?: string | null };

/** Pide validar el número y enviar el WhatsApp de confirmación en vivo. Nunca lanza error. */
export async function verifyWebinarPhone(token: string): Promise<VerifyResult> {
  try {
    const { data, error } = await supabase.functions.invoke("webinar-verify", { body: { token } });
    if (error || !data) return { status: "unavailable" };
    return data as VerifyResult;
  } catch {
    return { status: "unavailable" };
  }
}

export async function fetchPhoneStatus(token: string): Promise<{ status: PhoneStatus; error: string | null }> {
  try {
    const { data } = await supabase.rpc("webinar_phone_status", { p_token: token });
    const d = (data ?? {}) as { status?: string; error?: string | null };
    return { status: (d.status as PhoneStatus) ?? "unknown", error: d.error ?? null };
  } catch {
    return { status: "unknown", error: null };
  }
}

/** Corrige el número cuando el WhatsApp no pudo entregarse. */
export async function updateWebinarPhone(token: string, phone: string, country: string): Promise<void> {
  const { error } = await supabase.rpc("webinar_update_phone", { p_token: token, p_phone: phone, p_country: country });
  if (error) {
    const msg = error.message || "";
    if (msg.includes("phone_in_use")) throw new Error("Ese número ya está registrado.");
    if (msg.includes("invalid_phone")) throw new Error("Revisa tu número de WhatsApp.");
    throw new Error("No pudimos actualizar tu número. Intenta de nuevo.");
  }
}

/** Enlace único del socio al webinar. `src` identifica el canal (whatsapp, instagram…). */
export function webinarLink(code: string, src?: string | null): string {
  const base = `${window.location.origin}/webinar?ref=${encodeURIComponent(code)}`;
  return src ? `${base}&src=${encodeURIComponent(src)}` : base;
}

export async function resumeWebinarLead(token: string): Promise<WebinarLead | null> {
  const { data, error } = await supabase.rpc("webinar_resume", { p_token: token });
  if (error) return null;
  return toLead(data as RawState);
}

/** Envía un evento. Nunca lanza error: el seguimiento no debe interrumpir el video. */
export async function trackWebinar(token: string, kind: WebinarTrackKind, percent?: number, position?: number): Promise<void> {
  try {
    await supabase.rpc("webinar_track", {
      p_token: token,
      p_kind: kind,
      p_percent: percent === undefined ? null : Math.round(percent),
      p_position: position === undefined ? null : Math.round(position),
    });
  } catch {
    // se ignora
  }
}

// ---------------------------------------------------------------------------
// Administración
// ---------------------------------------------------------------------------

export type WebinarSettings = {
  enabled: boolean;
  title: string;
  subtitle: string;
  videoUrl: string;
  videoDuration: number | null;
  requireRegistration: boolean;
  captureMode: "before_video" | "mid_video";
  captureAtPercent: number;
  nudgesEnabled: boolean;
  maxNudges: number;
  sendFromHour: number;
  sendToHour: number;
};

export type NudgeRule = {
  id: string;
  bucket: 25 | 50 | 75;
  step: number;
  delayMinutes: number;
  template: string;
  templateName: string;
  enabled: boolean;
};

export type WebinarOverview = {
  leads: number;
  optedIn: number;
  started: number;
  p25: number;
  p50: number;
  p75: number;
  completed: number;
  hot: number;
  ctaTest: number;
  ctaCommunity: number;
  nudgesSent: number;
  nudgesPending: number;
  nudgesFailed: number;
  recovered: number;
  recoveredCompleted: number;
  byTemp: Record<string, number>;
};

export type WebinarLeadRow = {
  id: string;
  name: string;
  phoneLast4: string;
  country: string | null;
  maxPercent: number;
  score: number;
  temperature: string;
  optedIn: boolean;
  completed: boolean;
  lastActivity: string;
  createdAt: string;
  sponsorName: string | null;
  source: string | null;
};

export async function fetchWebinarSettings(): Promise<WebinarSettings | null> {
  const { data, error } = await supabase.from("webinar_settings").select("*").maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    enabled: data.enabled,
    title: data.title,
    subtitle: data.subtitle,
    videoUrl: data.video_url ?? "",
    videoDuration: data.video_duration_seconds,
    requireRegistration: data.require_registration,
    captureMode: data.capture_mode === "before_video" ? "before_video" : "mid_video",
    captureAtPercent: data.capture_at_percent ?? 25,
    nudgesEnabled: data.nudges_enabled,
    maxNudges: data.max_nudges,
    sendFromHour: data.send_from_hour,
    sendToHour: data.send_to_hour,
  };
}

export async function saveWebinarSettings(s: WebinarSettings): Promise<void> {
  const { data: org, error: e0 } = await supabase.from("webinar_settings").select("organization_id").maybeSingle();
  if (e0 || !org) throw e0 ?? new Error("Sin organización");
  const { error } = await supabase
    .from("webinar_settings")
    .update({
      enabled: s.enabled,
      title: s.title,
      subtitle: s.subtitle,
      video_url: s.videoUrl.trim() || null,
      video_duration_seconds: s.videoDuration,
      require_registration: s.requireRegistration,
      capture_mode: s.captureMode,
      capture_at_percent: Math.min(90, Math.max(5, Math.round(s.captureAtPercent))),
      nudges_enabled: s.nudgesEnabled,
      max_nudges: s.maxNudges,
      send_from_hour: s.sendFromHour,
      send_to_hour: s.sendToHour,
    })
    .eq("organization_id", org.organization_id);
  if (error) throw error;
}

export async function fetchNudgeRules(): Promise<NudgeRule[]> {
  const { data, error } = await supabase.from("webinar_nudge_rules").select("*").order("bucket").order("step");
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    bucket: r.bucket,
    step: r.step,
    delayMinutes: r.delay_minutes,
    template: r.template,
    templateName: r.template_name ?? "",
    enabled: r.enabled,
  }));
}

export async function saveNudgeRule(r: NudgeRule): Promise<void> {
  const { error } = await supabase
    .from("webinar_nudge_rules")
    .update({ delay_minutes: r.delayMinutes, template: r.template, template_name: r.templateName.trim() || null, enabled: r.enabled })
    .eq("id", r.id);
  if (error) throw error;
}

export async function fetchWebinarOverview(days = 30): Promise<WebinarOverview> {
  const { data, error } = await supabase.rpc("webinar_admin_overview", { p_days: days });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, number & Record<string, number>>;
  return {
    leads: d.leads ?? 0,
    optedIn: d.opted_in ?? 0,
    started: d.started ?? 0,
    p25: d.p25 ?? 0,
    p50: d.p50 ?? 0,
    p75: d.p75 ?? 0,
    completed: d.completed ?? 0,
    hot: d.hot ?? 0,
    ctaTest: d.cta_test ?? 0,
    ctaCommunity: d.cta_community ?? 0,
    nudgesSent: d.nudges_sent ?? 0,
    nudgesPending: d.nudges_pending ?? 0,
    nudgesFailed: d.nudges_failed ?? 0,
    recovered: d.recovered ?? 0,
    recoveredCompleted: d.recovered_completed ?? 0,
    byTemp: (d.by_temp as unknown as Record<string, number>) ?? {},
  };
}

export async function fetchWebinarLeads(limit = 100): Promise<WebinarLeadRow[]> {
  const { data, error } = await supabase
    .from("webinar_leads")
    .select("id, full_name, phone, country, max_percent, score, temperature, whatsapp_opt_in, opted_out_at, video_completed_at, last_activity_at, created_at, source, sponsor:profiles!webinar_leads_sponsor_id_fkey(full_name)")
    .order("last_activity_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const sp = r.sponsor as unknown as { full_name: string | null } | { full_name: string | null }[] | null;
    const sponsor = Array.isArray(sp) ? sp[0] : sp;
    return {
      id: r.id,
      name: r.full_name,
      phoneLast4: String(r.phone).slice(-4),
      country: r.country,
      maxPercent: r.max_percent,
      score: r.score,
      temperature: r.temperature,
      optedIn: r.whatsapp_opt_in && !r.opted_out_at,
      completed: !!r.video_completed_at,
      lastActivity: r.last_activity_at,
      createdAt: r.created_at,
      sponsorName: sponsor?.full_name ?? null,
      source: (r as { source?: string | null }).source ?? null,
    };
  });
}

export const TEMP_META: Record<string, { label: string; cls: string }> = {
  frio: { label: "Frío", cls: "bg-slate-100 text-slate-600" },
  curioso: { label: "Curioso", cls: "bg-sky-100 text-sky-700" },
  tibio: { label: "Tibio", cls: "bg-amber-100 text-amber-700" },
  caliente: { label: "Caliente", cls: "bg-orange-100 text-orange-700" },
  muy_caliente: { label: "Muy caliente", cls: "bg-rose-100 text-rose-700" },
};

// ---------------------------------------------------------------------------
// Panel del afiliado: sus propios invitados al webinar
// ---------------------------------------------------------------------------

export type MyWebinarStats = {
  leads: number;
  started: number;
  p50: number;
  completed: number;
  hot: number;
  bySource: Record<string, number>;
};

export type MyWebinarLead = {
  id: string;
  token: string;
  name: string;
  phone: string;
  country: string | null;
  maxPercent: number;
  temperature: string;
  completed: boolean;
  optedOut: boolean;
  source: string | null;
  lastActivity: string;
  createdAt: string;
};

export const SOURCE_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  telegram: "Telegram",
  link: "Link directo",
  qr: "Código QR",
  directo: "Sin canal",
};

export async function fetchMyWebinarStats(days = 30): Promise<MyWebinarStats> {
  const { data, error } = await supabase.rpc("webinar_my_stats", { p_days: days });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    leads: (d.leads as number) ?? 0,
    started: (d.started as number) ?? 0,
    p50: (d.p50 as number) ?? 0,
    completed: (d.completed as number) ?? 0,
    hot: (d.hot as number) ?? 0,
    bySource: (d.by_source as Record<string, number>) ?? {},
  };
}

export async function fetchMyWebinarLeads(limit = 100): Promise<MyWebinarLead[]> {
  const { data, error } = await supabase
    .from("webinar_leads")
    .select("id, token, full_name, phone, country, max_percent, temperature, video_completed_at, opted_out_at, source, last_activity_at, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    token: r.token,
    name: r.full_name,
    phone: String(r.phone),
    country: r.country,
    maxPercent: r.max_percent,
    temperature: r.temperature,
    completed: !!r.video_completed_at,
    optedOut: !!r.opted_out_at,
    source: r.source ?? null,
    lastActivity: r.last_activity_at,
    createdAt: r.created_at,
  }));
}

/** Mensaje sugerido para que el socio escriba personalmente a su invitado. */
export function suggestedWebinarMessage(l: MyWebinarLead): string {
  const first = l.name.split(" ")[0];
  const resume = `${window.location.origin}/webinar?t=${l.token}`;
  if (l.completed) return `Hola ${first} 😊 Vi que terminaste el webinar de Vívelo AI. ¿Qué te pareció? Si quieres, te cuento los siguientes pasos.`;
  if (l.maxPercent > 0) return `Hola ${first} 😊 Vi que ya llevas el ${l.maxPercent}% del webinar de Vívelo AI. Aquí puedes continuar justo donde lo dejaste: ${resume}`;
  return `Hola ${first} 😊 Gracias por registrarte al webinar de Vívelo AI. Cuando tengas unos minutos, aquí lo puedes ver: ${resume}`;
}

/** Código de referido del socio con sesión iniciada. */
export async function fetchMyReferralCode(): Promise<string | null> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return null;
  const { data } = await supabase.from("profiles").select("referral_code").eq("id", u.user.id).maybeSingle();
  return (data?.referral_code as string | null) ?? null;
}

export type ProspectForImport = { id: string; name: string; network: string | null; source: string | null; campaign: string | null; niche: string | null };

/** Prospectos del socio (p. ej. captados con el scraper) para convertirlos en contactos de envío. */
export async function fetchProspectsForImport(limit = 300): Promise<ProspectForImport[]> {
  const { data, error } = await supabase
    .from("prospects")
    .select("id, display_name, username, network, source, source_campaign, niche")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    name: r.display_name || r.username || "Sin nombre",
    network: r.network ?? null,
    source: r.source ?? null,
    campaign: r.source_campaign ?? null,
    niche: r.niche ?? null,
  }));
}

/** Código del prospecto que viene en el link (?p=...), si existe. */
function prospectCodeFromUrl(): string | null {
  try {
    const c = (new URLSearchParams(window.location.search).get("p") || "").trim().toLowerCase();
    return /^[a-z0-9]{6,12}$/.test(c) ? c : null;
  } catch {
    return null;
  }
}

/** Cuenta una visita al webinar desde el link único de un prospecto (una vez por sesión del navegador). */
export async function trackProspectVisit(): Promise<void> {
  const code = prospectCodeFromUrl();
  if (!code) return;
  try {
    const key = `vv_p_click_${code}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* sin sessionStorage: se cuenta igual */
  }
  try {
    await supabase.rpc("prospect_link_click", { p_code: code });
  } catch {
    /* no bloquea la página */
  }
}

/** Marca que el prospecto se registró en el webinar desde su link único. */
export async function trackProspectRegistration(): Promise<void> {
  const code = prospectCodeFromUrl();
  if (!code) return;
  try {
    await supabase.rpc("prospect_link_register", { p_code: code });
  } catch {
    /* no bloquea el registro */
  }
}